﻿/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { useAuth } from "@/context/auth";
import { router } from "@/routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faOpenid } from "@fortawesome/free-brands-svg-icons";
import { getApiUrl } from "@/utils/baseUrl";
import { loginRequiresTotp } from "@/api/auth";
import logo from "@/assets/logo-mark.svg";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/utils/language";

// Error message mapping for cleaner code
const ERROR_MESSAGES: Record<string, string> = {
  "Invalid credentials": "Incorrect username or password",
  "Invalid request data": "Please check your input and try again",
  "Failed to get user": "Unable to verify user credentials",
  "Failed to generate session token": "Authentication failed, please try again",
};

const OIDC_ERROR_MESSAGES: Record<string, string> = {
  oidc_unavailable: "OpenID Connect sign-in is currently unavailable. Use local credentials if you have them.",
};

function getInitialLoginError(): string {
  const errorCode = new URLSearchParams(window.location.search).get("error");
  if (!errorCode) {
    return "";
  }

  return OIDC_ERROR_MESSAGES[errorCode] || "Unable to sign in with OpenID Connect.";
}

export default function Login() {
  const { login, verifyTotp, checkRegistrationStatus } = useAuth();
  const { t } = useLanguage();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(getInitialLoginError);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Survive PWA background-kills on iOS: persist the pending token across page
  // reloads so the TOTP step doesn't vanish when the user switches to their
  // authenticator app and the OS reloads the page.
  const [pendingToken, _setPendingToken] = useState<string | null>(
    () => sessionStorage.getItem("nyrvex-pending-totp") ?? null
  );
  const setPendingToken = (t: string | null) => {
    if (t) sessionStorage.setItem("nyrvex-pending-totp", t);
    else sessionStorage.removeItem("nyrvex-pending-totp");
    _setPendingToken(t);
  };
  const [totpCode, setTotpCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [isVerifyingTotp, setIsVerifyingTotp] = useState(false);
  const [authOptions, setAuthOptions] = useState({
    hasUsers: true,
    oidcConfigured: false,
    oidcReady: false,
  });

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await checkRegistrationStatus();
        setAuthOptions({
          hasUsers: status.hasUsers,
          oidcConfigured: status.oidcConfigured,
          oidcReady: status.oidcReady,
        });
      } catch (err) {
        console.error("Failed to check registration status:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [checkRegistrationStatus]);

  const handleOIDCLogin = () => {
    window.location.href = getApiUrl("/auth/oidc/login");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const result = await login(username, password);
      if (loginRequiresTotp(result)) {
        setPendingToken(result.pendingToken);
      } else {
        localStorage.setItem("nyrvex-active-tab", "home");
        router.navigate({ to: "/" });
      }
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage = err.message;

        setError(ERROR_MESSAGES[errorMessage] || "An error occurred while signing in");
      } else {
        setError("Unable to sign in at this time");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!pendingToken) return;

    setIsVerifyingTotp(true);
    try {
      await verifyTotp(pendingToken, totpCode.trim());
      setPendingToken(null); // clear sessionStorage
      localStorage.setItem("nyrvex-active-tab", "home");
      router.navigate({ to: "/" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setIsVerifyingTotp(false);
    }
  };

  // Subtitle reflects the current step so the branded header stays meaningful.
  const subtitle = pendingToken
    ? "Two-factor authentication"
    : !authOptions.hasUsers
      ? "Welcome to your panel"
      : "Sign in to your panel";

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 pattern overflow-hidden px-4 sm:px-6">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--accent-500)" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 pattern overflow-hidden m-0 px-4 sm:px-6 relative">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
        className="w-full max-w-md relative"
      >
        <Card className="w-full bg-white/95 dark:bg-gray-850/95 border-gray-200 dark:border-gray-800 shadow-xl">
          <CardHeader className="text-center">
            <img
              src={logo}
              alt="Nyrvex"
              className="h-16 w-16 mx-auto mb-2 select-none pointer-events-none"
            />
            <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white pointer-events-none select-none">
              Nyrvex
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">{subtitle}</CardDescription>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {pendingToken ? (
                <form onSubmit={handleTotpSubmit} className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    {useBackupCode
                      ? "Enter one of your backup codes"
                      : "Enter the 6-digit code from your authenticator app"}
                  </p>
                  <div>
                    <Label htmlFor="totp-code" className="sr-only">
                      {useBackupCode ? "Backup code" : "Authentication code"}
                    </Label>
                    <Input
                      id="totp-code"
                      name="totp-code"
                      type="text"
                      inputMode={useBackupCode ? "text" : "numeric"}
                      autoComplete="one-time-code"
                      autoFocus
                      required
                      placeholder={useBackupCode ? "XXXX-XXXX" : "123456"}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      className={cn(
                        "text-center tracking-widest dark:bg-gray-800 dark:border-gray-700",
                        error && "border-red-500 dark:border-red-500"
                      )}
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg" disabled={isVerifyingTotp} isLoading={isVerifyingTotp}>
                    Verify
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setUseBackupCode((v) => !v);
                        setTotpCode("");
                        setError("");
                      }}
                      className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 underline"
                    >
                      {useBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPendingToken(null);
                        setTotpCode("");
                        setPassword("");
                        setError("");
                      }}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline"
                    >
                      Back
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {authOptions.oidcReady && (
                    <Button
                      onClick={handleOIDCLogin}
                      variant="outline"
                      className="w-full border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                      size="lg"
                    >
                      <span className="flex items-center" aria-label="Sign in with OpenID">
                        Sign in with
                        <FontAwesomeIcon icon={faOpenid} className="text-lg ml-2" aria-hidden="true" />
                      </span>
                    </Button>
                  )}

                  {authOptions.oidcReady && authOptions.hasUsers && (
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                        <span className="bg-white dark:bg-gray-850 px-3">or</span>
                      </div>
                    </div>
                  )}

                  {authOptions.hasUsers && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="username" className="sr-only">
                            {t("username")}
                          </Label>
                          <Input
                            id="username"
                            name="username"
                            type="text"
                            autoComplete="username"
                            required
                            disabled={isSubmitting}
                            placeholder={t("username")}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className={cn(
                              "dark:bg-gray-800 dark:border-gray-700",
                              error && "border-red-500 dark:border-red-500"
                            )}
                          />
                        </div>
                        <div>
                          <Label htmlFor="password" className="sr-only">
                            {t("password")}
                          </Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            disabled={isSubmitting}
                            placeholder={t("password")}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={cn(
                              "dark:bg-gray-800 dark:border-gray-700",
                              error && "border-red-500 dark:border-red-500"
                            )}
                          />
                        </div>
                      </div>

                      <Button type="submit" className="w-full" size="lg" disabled={isSubmitting} isLoading={isSubmitting}>
                        {t("signIn")}
                      </Button>
                    </form>
                  )}

                  {!authOptions.hasUsers && authOptions.oidcConfigured && !authOptions.oidcReady && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                      OpenID Connect is configured but the provider is unavailable right now.
                    </div>
                  )}

                  {!authOptions.hasUsers && !authOptions.oidcConfigured && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                      <p>{t("noAdminUser")}</p>
                      <code className="mt-2 block rounded bg-amber-100/80 px-2 py-1.5 font-mono text-[12px] text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                        nyrvex create-user &lt;username&gt;
                      </code>
                      <p className="mt-1.5 text-xs opacity-80">
                        In Docker: <code className="font-mono">docker exec -it nyrvex nyrvex create-user &lt;username&gt;</code>
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
